import { useState, useEffect, createContext, useContext, useCallback, useRef } from 'react';
import { authApi } from '../utils/api';
import posthog from 'posthog-js';

const AuthContext = createContext();

// Helper function to decode JWT and check expiration
const isTokenExpiringSoon = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const { exp } = JSON.parse(jsonPayload);
    const now = Date.now() / 1000;

    // Token expires in less than 1 day (86400 seconds)
    return exp - now < 86400;
  } catch (error) {
    return true; // If we can't decode, consider it expiring
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshIntervalRef = useRef(null);

  // Refresh token function
  const refreshToken = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      // Check if token is expiring soon
      if (isTokenExpiringSoon(token)) {
        console.log('Token expiring soon, refreshing...');
        const response = await authApi.refreshToken();
        localStorage.setItem('token', response.data.access_token);
        console.log('Token refreshed successfully');
      }
    } catch (error) {
      console.error('Failed to refresh token:', error);
      // Don't logout on refresh failure - let the token expire naturally
    }
  }, []);

  // Initial load - check token and get user
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      authApi.getMe()
        .then(response => {
          const userData = response.data;
          setUser(userData);
          posthog.identify(String(userData.id), {
            username: userData.username,
            email: userData.email,
            is_admin: userData.is_admin,
            is_trial: userData.is_trial,
          });
          // Start periodic token refresh
          refreshToken();
        })
        .catch((error) => {
          // Token is invalid - clear it and redirect will happen via interceptor
          localStorage.removeItem('token');
          setUser(null);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [refreshToken]);

  // Periodic token refresh check (every 6 hours)
  useEffect(() => {
    if (user) {
      refreshIntervalRef.current = setInterval(() => {
        refreshToken();
      }, 6 * 60 * 60 * 1000); // 6 hours

      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    }
  }, [user, refreshToken]);

  /**
   * Login user with username and password.
   *
   * Optimized to make only 1 API call - the login endpoint now returns
   * both the JWT token and user data in a single response.
   *
   * @param {string} username - User's username
   * @param {string} password - User's password
   * @returns {Promise<boolean>} True if login succeeds
   * @throws {Error} Throws error if credentials are invalid
   */
  const login = async (username, password) => {
    try {
      // Single API call that returns both token and user data
      const response = await authApi.login(username, password);
      const { access_token, user } = response.data;

      // Store token in localStorage
      localStorage.setItem('token', access_token);

      // Set user state immediately (no need for second API call)
      setUser(user);

      posthog.identify(String(user.id), {
        username: user.username,
        email: user.email,
        is_admin: user.is_admin,
        is_trial: user.is_trial,
      });
      posthog.capture('user_logged_in', { is_admin: user.is_admin, is_trial: user.is_trial });

      // Start token refresh monitoring
      refreshToken();

      return true;
    } catch (error) {
      throw error;
    }
  };

  /**
   * Register a new user account.
   *
   * This function sends registration data to the backend /auth/register endpoint.
   * The user is NOT automatically logged in after successful registration - they
   * must manually login using the /login page.
   *
   * @param {Object} userData - User registration data
   * @param {string} userData.username - Username (3-50 chars, alphanumeric and underscores)
   * @param {string} userData.email - Valid email address
   * @param {string} userData.password - Password meeting all validation requirements
   *
   * @returns {Promise<boolean>} True if registration succeeds
   *
   * @throws {Error} Throws error if:
   *   - Username already exists (400)
   *   - Email already registered (400)
   *   - Network error
   *   - Server error (500)
   *
   * Example usage:
   *   const { register } = useAuth();
   *   try {
   *     await register({ username: 'john', email: 'john@example.com', password: 'SecurePass123!' });
   *     navigate('/login', { state: { message: 'Registration successful!' } });
   *   } catch (error) {
   *     setError(error.response?.data?.detail || 'Registration failed');
   *   }
   */
  const register = async (userData) => {
    try {
      // Send registration request to backend
      // Backend will validate uniqueness and create user account
      await authApi.register(userData);

      // Return success - user must login separately
      return true;
    } catch (error) {
      // Re-throw error for component to handle
      // Error contains: error.response.data.detail (e.g., "Username already registered")
      throw error;
    }
  };

  const logout = () => {
    // Clear refresh interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }
    posthog.capture('user_logged_out');
    posthog.reset();
    localStorage.removeItem('token');
    setUser(null);
  };

  const value = {
    user,
    login,
    register,
    logout,
    refreshToken,
    loading,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};